import { Box, Modal, Typography } from "@mui/material";
import { useTheme } from "../context/ThemeContext";
import { styledComponent } from "../utils/styled";
import {
  ConfirmDialogBox,
  ConfirmDialogTextGroup,
  ConfirmDialogTitle,
  ConfirmDialogMessage,
  NeutralGradientBtn,
} from "./ui";

// Dark/light intentionally use different wrap widths here, not a mismatch.
const MessageText = styledComponent(ConfirmDialogMessage)(({ isDark }) => ({
  maxWidth: isDark ? "392px" : "418px",
}));

const StatCardRow = styledComponent(Box)(({ isDark }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "32px",
  padding: "17px 25px",
  borderRadius: "14px",
  border: "1px solid",
  borderColor: isDark ? "rgba(49,54,59,0.8)" : "rgba(49,54,59,0.1)",
  backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
  width: "fit-content",
  marginTop: isDark ? "24px" : "12px",
}));

const StatDivider = styledComponent(Box)(({ isDark }) => ({
  width: "1px",
  height: "32px",
  flexShrink: 0,
  backgroundColor: isDark ? "rgba(49,54,59,0.9)" : "rgba(49,54,59,0.1)",
}));

const StatCellRoot = styledComponent(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "3px",
});

const StatCellLabel = styledComponent(Typography)({
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "11px",
  lineHeight: "16px",
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  textAlign: "center",
  whiteSpace: "nowrap",
  color: "#7E848D",
});

const StatCellValue = styledComponent(Typography)(({ isDark, accent }) => ({
  fontFamily: "Inter",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "22px",
  textAlign: "center",
  whiteSpace: "nowrap",
  color: accent
    ? isDark
      ? "#5FB878"
      : "#146E37"
    : isDark
      ? "#F1F4F8"
      : "#434950",
}));

function StatCell({ label, value, isDark, accent }) {
  return (
    <StatCellRoot>
      <StatCellLabel>{label}</StatCellLabel>
      <StatCellValue isDark={isDark} accent={accent}>
        {value}
      </StatCellValue>
    </StatCellRoot>
  );
}

const ButtonRow = styledComponent(Box)(({ isDark }) => ({
  display: "flex",
  justifyContent: "center",
  width: "100%",
  marginTop: isDark ? "24px" : "32px",
}));

// Shown after every successful "Review and Submit".
export function ProcedureSubmittedModal({
  open,
  procedureName,
  dateStarted,
  dateCompleted,
  duration,
  stepsCompleted,
  totalSteps,
  onClose,
}) {
  const { isDark } = useTheme();

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <ConfirmDialogBox isDark={isDark} sx={{ width: "fit-content", gap: 0 }}>
        <ConfirmDialogTextGroup>
          <ConfirmDialogTitle isDark={isDark}>
            Procedure Submitted
          </ConfirmDialogTitle>
          <MessageText isDark={isDark}>
            {procedureName} has been successfully submitted and recorded.
          </MessageText>
        </ConfirmDialogTextGroup>

        <StatCardRow isDark={isDark}>
          <StatCell label='Date Started' value={dateStarted} isDark={isDark} />
          <StatDivider isDark={isDark} />
          <StatCell
            label='Date Completed'
            value={dateCompleted}
            isDark={isDark}
          />
          <StatDivider isDark={isDark} />
          <StatCell label='Duration' value={duration} isDark={isDark} />
          <StatDivider isDark={isDark} />
          <StatCell
            label='Steps'
            value={`${stepsCompleted} / ${totalSteps}`}
            isDark={isDark}
            accent
          />
        </StatCardRow>

        <ButtonRow isDark={isDark}>
          <NeutralGradientBtn isDark={isDark} onClick={onClose}>
            Close
          </NeutralGradientBtn>
        </ButtonRow>
      </ConfirmDialogBox>
    </Modal>
  );
}
